/**
 * Note dell'ordine scritte nel negozio (WooCommerce: nota privata o «Inviata al cliente»), in sola lettura.
 * Le copia flusia-core in order_store_notes a ogni aggiornamento dell'ordine (sync/orders/orderNotes.ts);
 * si modificano solo nel negozio. Si mostra solo per gli ordini con commerce_store_id.
 * Tabella non ancora creata (migration 20261022_order_store_notes) o nessuna nota: niente a schermo.
 */
import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { formatDate } from '../../../lib/utils';

interface StoreNote {
  id: string;
  body: string;
  to_customer: boolean;
  author: string | null;
  noted_at: string | null;
}

/** "system" = nota scritta da WooCommerce stesso (cambi di stato, pagamenti). */
const authorLabel = (a: string | null) => (!a ? null : a === 'system' ? 'WooCommerce' : a);

export function StoreOrderNotes({ orderId, refreshKey }: { orderId: string; refreshKey?: string }) {
  const [notes, setNotes] = useState<StoreNote[]>([]);

  useEffect(() => {
    let alive = true;
    supabase
      .from('order_store_notes')
      .select('id, body, to_customer, author, noted_at')
      .eq('order_id', orderId)
      .order('noted_at', { ascending: false })
      .then(({ data, error }) => {
        if (alive) setNotes(error ? [] : ((data || []) as StoreNote[]));
      });
    return () => { alive = false; };
  }, [orderId, refreshKey]);

  if (notes.length === 0) return null;

  return (
    <section className="space-y-2">
      <h3 className="text-sm font-medium text-[#37352F] dark:text-white">Note dal negozio</h3>
      <div className="space-y-1.5">
        {notes.map((n) => (
          <div
            key={n.id}
            className={`rounded-lg px-3 py-2.5 ${n.to_customer ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-[rgba(55,53,47,0.04)] dark:bg-gray-800'}`}
          >
            <p className="text-xs font-medium mb-0.5 flex flex-wrap gap-x-2">
              <span className={n.to_customer ? 'text-blue-700 dark:text-blue-300' : 'text-[#787774] dark:text-gray-400'}>
                {n.to_customer ? 'Inviata al cliente' : 'Nota privata'}
              </span>
              <span className="font-normal text-[#9B9A97] dark:text-gray-500">
                {[authorLabel(n.author), formatDate(n.noted_at, { hour: '2-digit', minute: '2-digit' })].filter(Boolean).join(' · ')}
              </span>
            </p>
            {/* le note di WooCommerce possono contenere HTML (link, grassetti): qui solo il testo */}
            <p className="text-sm text-[#37352F] dark:text-white whitespace-pre-line">{n.body.replace(/<[^>]*>/g, '')}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-[#9B9A97] dark:text-gray-500">Si modificano nel negozio.</p>
    </section>
  );
}
